import React, { useState } from "react";
import classNames from "classnames/bind";
import { Row, Space, Button, Card } from "@masterteam/mt-design-react";

import { useIsAr } from "../../Hooks";

import { ReactComponent as ArrowLeft } from "../../assets/images/arrow-left.svg";
import { ReactComponent as ArrowRight } from "../../assets/images/arrow-right.svg";
import { ReactComponent as LevelOneIcon } from "../../assets/images/level-one.svg";
import { ReactComponent as LevelTwoIcon } from "../../assets/images/level-two.svg";

import styles from "./Table.module.scss";

const cx = classNames.bind(styles);

const pageSize = 10;

function getPagesCount(list) {
  return Math.ceil(list.length / pageSize);
}

function NewTable({
  data = [],
  columns = [],
  actions,
  actionsHeader,
  onClickItem,
  transparent,
  paginated,
  compact,
  scrollable,
  differentRowsColor = false,
  headerHasBackgroundColor = false,
  headerStyle = {},
  isGroupedByObjectives = false,
}) {
  const [isAr] = useIsAr();
  const [currentPage, setCurrentPage] = useState(1);
  const pagesCount = getPagesCount(data);

  function navigatePrevious() {
    if (currentPage > 1) {
      setCurrentPage((currentVal) => currentVal - 1);
    }
  }

  function navigateNext() {
    if (currentPage < pagesCount) {
      setCurrentPage((currentVal) => currentVal + 1);
    }
  }

  function navigateToPage(value) {
    setCurrentPage(value);
  }

  let paginationItems = [];
  let endIndex = pageSize * currentPage;
  let startIndex = endIndex - pageSize;

  //this iteration will draw pagination items
  for (let i = 1; i <= pagesCount; i++) {
    paginationItems.push(
      <Button
        id={i}
        variant={i === currentPage ? "secondary" : "dark"}
        key={i}
        onClick={() => navigateToPage(i)}
      >
        {i}
      </Button>
    );
  }

  const renderRows = (list) => {
    return list.map((item, index) =>
      !paginated || (index >= startIndex && index < endIndex) ? (
        <Row
          align="center"
          size="medium"
          key={item.key || `item${index}`}
          className={cx({
            row: true,
            pointer: onClickItem,
            differentRowsColor: differentRowsColor,
          })}
          onClick={onClickItem ? () => onClickItem(item, index) : undefined}
        >
          {columns.map((col, index) => (
            <div key={col.key} style={{ width: col.width, textAlign: col.textAlign ?? 'start' }}>
              {typeof col.cell === "function"
                ? col.cell(item)
                : item?.[col.cell]}
            </div>
          ))}
          {actions?.length ? (
            <Row
              size="small"
              justify="end"
              style={{ width: "10%" }}
              onClick={(e) => e.stopPropagation()}
            >
              {actions.map((action, i) => {
                if (action.hidden?.(item)) return null;
                if (typeof action === "function") return action(item);
                const isLast = index !== 0 && index === list?.length - 1;
                return (
                  <Button
                    key={i}
                    shape="square"
                    fill="none"
                    variant="dark"
                    {...action}
                    tooltip-position={isLast ? "top" : "buttom"}
                    disabled={action.disabled?.(item)}
                    onClick={() => action.onClick(item)}
                  >
                    {action.label}
                  </Button>
                );
              })}
            </Row>
          ) : null}
        </Row>
      ) : null
    );
  };

  return (
    <Space direction="vertical" size="medium" className={styles.container}>
      <Card
        className={cx({
          table: true,
          transparent,
          compact,
        })}
      >
        <Row
          align="center"
          size="medium"
          className={styles.header}
          style={{
            color: headerHasBackgroundColor && "white",
            backgroundColor: headerHasBackgroundColor && "var(--primary-color)",
          }}
        >
          {columns.map((col, index) => (
            <div key={col.key} style={{ width: col.width, ...headerStyle }}>
              {typeof col.header === "function" ? col.header() : col.header}
            </div>
          ))}
          {actions?.length ? (
            <Row justify="end" style={{ width: "10%" }}>
              {actionsHeader}
            </Row>
          ) : null}
        </Row>
        <div
          className={cx({
            scrollable,
          })}
        >
          {isGroupedByObjectives
            ? data.map((element, elementIndex) => (
                <>
                  <Row
                    key={elementIndex}
                    className={styles.groupTitle}
                    align="center"
                    size="small"
                  >
                    {+element?.GroupInfo?.Level === 1 ? (
                      <LevelOneIcon fill={element?.GroupInfo?.ParentColor} />
                    ) : (
                      <LevelTwoIcon fill={element?.GroupInfo?.ParentColor} />
                    )}
                    <div>
                      {isAr
                        ? element?.GroupInfo?.ArabicName
                        : element?.GroupInfo?.EnglishName}
                    </div>
                  </Row>
                  {renderRows(element?.List)}
                </>
              ))
            : renderRows(data)}
        </div>
      </Card>
      {!paginated || pagesCount < 2 ? null : (
        <Row justify="center" align="center" size="small" id="usersPagination">
          <Button
            id="0"
            fill="none"
            variant="dark"
            key="pagePrev"
            Icon={ArrowLeft}
            disabled={currentPage === 1}
            onClick={navigatePrevious}
          />
          {paginationItems}
          <Button
            id={pagesCount + 1}
            fill="none"
            variant="dark"
            key="pageNext"
            Icon={ArrowRight}
            disabled={currentPage === pagesCount}
            onClick={navigateNext}
          />
        </Row>
      )}
    </Space>
  );
}

export default NewTable;
